import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, CheckSquare, Square, Plus, Sparkle, PencilSimple, Trash, X,
  CheckCircle, LockKey, ArrowClockwise, ArrowRight, CaretDown, Bell,
} from "@phosphor-icons/react";
import { useAI } from "@/context/AIContext";
import StatusPill from "@/components/StatusPill";
import RoadmapStrip from "@/components/RoadmapStrip";
import RequisitosLocales from "@/components/RequisitosLocales";

function computeCurrentIndex(stages = []) {
  const idx = stages.findIndex((s) => s.status !== "completada");
  return idx; // -1 when every stage is completada (all-done edge case)
}

// Consistent short label helper (mirrors RoadmapStrip logic).
function shortStageLabel(name, index) {
  if (!name) return `Etapa ${index + 1}`;
  const firstPart = String(name).trim().split(/[:·\-–—]/)[0].trim();
  if (firstPart && firstPart.length <= 16) return firstPart;
  return `Etapa ${index + 1}`;
}

export default function PlaybookDetail() {
  const { id } = useParams();
  const [pb, setPb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [notesFor, setNotesFor] = useState(null);
  const [notesInput, setNotesInput] = useState("");
  const [newStageOpen, setNewStageOpen] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [newMilestone, setNewMilestone] = useState({});
  // When user chooses "Ver detalle" on a past stage, we let them expand it read-only.
  const [expandedPast, setExpandedPast] = useState({}); // stageId -> bool
  const stageRefs = useRef({}); // stageId -> element
  const { openDrawer } = useAI();

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/playbooks/${id}`)
      .then((r) => { setPb(r.data); setNameInput(r.data.name); })
      .catch(() => toast.error("Playbook no encontrado"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(load, [load]);

  const currentIndex = useMemo(() => (pb ? computeCurrentIndex(pb.stages) : 0), [pb]);
  const totalMs = pb ? pb.stages.reduce((a, s) => a + s.milestones.length, 0) : 0;
  const doneMs = pb ? pb.stages.reduce((a, s) => a + s.milestones.filter((m) => m.completed).length, 0) : 0;
  const pct = totalMs ? Math.round((doneMs / totalMs) * 100) : 0;
  const allDone = pb && pb.stages.length > 0 && pb.stages.every((s) => s.status === "completada");

  // Fire a toast when a NEW stage unlocks (currentIndex advances forward).
  const lastIndexRef = useRef(null);
  useEffect(() => {
    if (!pb || pb.stages.length === 0) return;
    if (lastIndexRef.current === null) {
      lastIndexRef.current = currentIndex;
      return;
    }
    if (currentIndex > lastIndexRef.current && currentIndex < pb.stages.length) {
      const s = pb.stages[currentIndex];
      const label = shortStageLabel(s?.name, currentIndex);
      toast.success(`Etapa desbloqueada: ${label}`, {
        description: "Sigue avanzando — completa los hitos de esta etapa.",
        duration: 5000,
      });
    }
    lastIndexRef.current = currentIndex;
  }, [currentIndex, pb]);

  const jumpToStage = (i) => {
    const s = pb?.stages?.[i];
    if (!s) return;
    const el = stageRefs.current[s.id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (i < currentIndex) setExpandedPast((p) => ({ ...p, [s.id]: true }));
  };

  const toggleMilestone = async (stage, m) => {
    try {
      const { data } = await api.put(
        `/playbooks/${pb.id}/stages/${stage.id}/milestones/${m.id}`,
        { completed: !m.completed }
      );
      setPb(data);
    } catch { toast.error("No se pudo actualizar"); }
  };

  const saveName = async () => {
    try {
      const { data } = await api.put(`/playbooks/${pb.id}`, { name: nameInput.trim() || pb.name });
      setPb(data); setEditingName(false); toast.success("Nombre actualizado");
    } catch { toast.error("No se pudo guardar"); }
  };

  const saveNotes = async (stageId) => {
    try {
      const { data } = await api.put(`/playbooks/${pb.id}/stages/${stageId}`, { notes: notesInput });
      setPb(data); setNotesFor(null); toast.success("Nota guardada");
    } catch { toast.error("No se pudo guardar la nota"); }
  };

  const addStage = async () => {
    if (!newStageName.trim()) return;
    try {
      const { data } = await api.post(`/playbooks/${pb.id}/stages`, { name: newStageName.trim() });
      setPb(data); setNewStageName(""); setNewStageOpen(false);
    } catch { toast.error("No se pudo crear la etapa"); }
  };

  const removeStage = async (stageId) => {
    if (!window.confirm("¿Eliminar esta etapa?")) return;
    try {
      const { data } = await api.delete(`/playbooks/${pb.id}/stages/${stageId}`);
      setPb(data);
    } catch { toast.error("No se pudo eliminar"); }
  };

  const addMilestone = async (stageId) => {
    const name = (newMilestone[stageId] || "").trim();
    if (!name) return;
    try {
      const { data } = await api.post(`/playbooks/${pb.id}/stages/${stageId}/milestones`, { name });
      setPb(data);
      setNewMilestone((s) => ({ ...s, [stageId]: "" }));
    } catch { toast.error("No se pudo agregar el hito"); }
  };

  const reopenStage = async (stage) => {
    // Explicitly re-open a completed stage (revisar). Sets stage.status to en_progreso
    try {
      const { data } = await api.put(`/playbooks/${pb.id}/stages/${stage.id}`, { status: "en_progreso" });
      setPb(data);
      toast.success(`Etapa "${stage.name}" reabierta`);
    } catch { toast.error("No se pudo reabrir la etapa"); }
  };

  const askAIStage = (stage) => {
    openDrawer({
      prefill: `Estoy en la etapa "${stage.name}" del playbook "${pb.name}". Hitos pendientes: ${stage.milestones.filter((m) => !m.completed).map((m) => m.name).join(", ") || "—"}. ¿Cuál es mi siguiente paso concreto hoy?`,
    });
  };

  if (loading) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  if (!pb) return null;

  const stages = pb.stages;

  return (
    <div data-testid="page-playbook-detail">
      <Link to="/mis-rutas" className="btn-ghost inline-flex items-center gap-2 mb-6" data-testid="link-back-playbooks">
        <ArrowLeft size={14} weight="bold" /> Volver a mis rutas
      </Link>

      <header className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex-1">
          <div className="label-mono text-muted-foreground">{pb.category}</div>
          {editingName ? (
            <div className="flex items-center gap-2 mt-2">
              <input className="input-brut !py-2 max-w-md" value={nameInput} onChange={(e) => setNameInput(e.target.value)} data-testid="input-playbook-name" />
              <button className="btn-primary" onClick={saveName} data-testid="btn-save-name">Guardar</button>
              <button className="btn-ghost" onClick={() => { setEditingName(false); setNameInput(pb.name); }}>Cancelar</button>
            </div>
          ) : (
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <h1 className="display text-4xl md:text-5xl">{pb.name}</h1>
              <button onClick={() => setEditingName(true)} className="btn-ghost" data-testid="btn-edit-name" title="Editar nombre">
                <PencilSimple size={16} weight="bold" />
              </button>
            </div>
          )}
          {pb.description && <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{pb.description}</p>}
        </div>
        <div className="brut-card !py-4 !px-5 min-w-[220px]">
          <div className="label-mono text-muted-foreground">Progreso</div>
          <div className="display text-4xl mt-1" data-testid="pb-progress">{pct}%</div>
          <div className="mt-2 h-2 border-2 border-foreground">
            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-muted-foreground mt-2">{doneMs}/{totalMs} hitos</div>
        </div>
      </header>

      {/* Roadmap strip — always visible so users know where they are on the journey */}
      <div className="mb-6">
        <RoadmapStrip stages={stages} currentIndex={currentIndex} onStageClick={jumpToStage} />
      </div>

      {allDone && (
        <div
          className="brut-card mb-6 border-2 flex items-center gap-3"
          style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
          data-testid="pb-complete-banner"
        >
          <CheckCircle size={22} weight="fill" />
          <div>
            <div className="label-mono opacity-90">Ruta completada</div>
            <div className="font-semibold">Has terminado todas las etapas. ¡Felicidades!</div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {stages.map((s, idx) => {
          // Semantics: currentIndex is the first non-completada stage (or -1 when all done).
          // Everything strictly before currentIndex is past; strictly after is future.
          const isPast = allDone || idx < currentIndex;
          const isCurrent = !allDone && idx === currentIndex;
          const isFuture = !allDone && idx > currentIndex;
          const done = s.milestones.filter((m) => m.completed).length;
          const total = s.milestones.length;
          const stagePct = total ? Math.round((done / total) * 100) : 0;

          // -------------------- FUTURE (locked, preview only) --------------------
          if (isFuture) {
            return (
              <div
                key={s.id}
                ref={(el) => { stageRefs.current[s.id] = el; }}
                className="brut-card opacity-60"
                data-testid={`stage-${s.id}`}
                data-state="future"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <LockKey size={18} weight="bold" />
                    <div className="label-mono text-muted-foreground">Próximamente · Etapa {idx + 1}</div>
                  </div>
                </div>
                <div className="display text-2xl mt-2">{s.name}</div>
                {s.milestones.length > 0 && (
                  <div className="mt-3">
                    <div className="label-mono text-muted-foreground mb-2">Actividades clave</div>
                    <ul className="space-y-1.5 text-sm">
                      {s.milestones.slice(0, 4).map((m) => (
                        <li key={m.id} className="flex items-start gap-2">
                          <span className="inline-block w-1.5 h-1.5 mt-2 bg-foreground flex-shrink-0" />
                          <span>{m.name}</span>
                        </li>
                      ))}
                      {s.milestones.length > 4 && (
                        <li className="text-xs text-muted-foreground italic">y {s.milestones.length - 4} más…</li>
                      )}
                    </ul>
                  </div>
                )}
                <div className="mt-4 label-mono text-muted-foreground">
                  Se desbloqueará cuando termines la etapa actual.
                </div>
              </div>
            );
          }

          // -------------------- PAST (collapsed, read-only) --------------------
          if (isPast) {
            const isExpanded = !!expandedPast[s.id];
            return (
              <div
                key={s.id}
                ref={(el) => { stageRefs.current[s.id] = el; }}
                className="brut-card border-2"
                data-testid={`stage-${s.id}`}
                data-state="past"
                style={{ borderColor: "hsl(var(--foreground))" }}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={20} weight="fill" style={{ color: "#2A9D8F" }} />
                    <div>
                      <div className="label-mono text-muted-foreground">Completada · Etapa {idx + 1}</div>
                      <div className="display text-xl mt-1">{s.name}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setExpandedPast((p) => ({ ...p, [s.id]: !p[s.id] }))}
                      className="btn-ghost flex items-center gap-1"
                      data-testid={`btn-toggle-past-${s.id}`}
                    >
                      <CaretDown size={14} weight="bold" className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                      {isExpanded ? "Ocultar" : "Ver detalle"}
                    </button>
                    <button
                      onClick={() => reopenStage(s)}
                      className="btn-ghost flex items-center gap-1"
                      title="Reabrir esta etapa"
                      data-testid={`btn-reopen-${s.id}`}
                    >
                      <ArrowClockwise size={14} weight="bold" /> Reabrir
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-4">
                    <ul className="divide-y-2 divide-foreground border-t-2 border-b-2 border-foreground">
                      {s.milestones.map((m) => (
                        <li key={m.id} className="py-2.5 flex items-center gap-3 text-sm">
                          <CheckSquare size={18} weight="fill" style={{ color: "#2A9D8F" }} />
                          <span className="line-through opacity-70">{m.name}</span>
                        </li>
                      ))}
                    </ul>
                    {s.notes && (
                      <div className="mt-3">
                        <div className="label-mono text-muted-foreground mb-1">Notas</div>
                        <div className="text-sm p-2 border-2 border-foreground/40">{s.notes}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }

          // -------------------- CURRENT (interactive) --------------------
          const nextIdx = idx + 1 < stages.length ? idx + 1 : null;
          const nextStage = nextIdx !== null ? stages[nextIdx] : null;
          return (
            <div
              key={s.id}
              ref={(el) => { stageRefs.current[s.id] = el; }}
              className="brut-card border-2 shadow-[4px_4px_0_0_hsl(var(--primary))]"
              data-testid={`stage-${s.id}`}
              data-state="current"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div
                    className="label-mono px-2 py-0.5 border-2 border-foreground"
                    style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
                  >
                    Etapa actual · {idx + 1}
                  </div>
                  <StatusPill value={s.status} />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => askAIStage(s)}
                    className="btn-secondary flex items-center gap-2 !py-1.5 !px-3 !text-[11px]"
                    data-testid={`btn-ask-ai-stage-${s.id}`}
                  >
                    <Sparkle size={12} weight="bold" /> Obtener ayuda con este paso
                  </button>
                  <button onClick={() => removeStage(s.id)} className="btn-ghost" title="Eliminar etapa" data-testid={`btn-remove-stage-${s.id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>

              <div className="display text-2xl mt-2">{s.name}</div>

              <div className="mt-3 h-2 border-2 border-foreground">
                <div className="h-full bg-primary" style={{ width: `${stagePct}%` }} />
              </div>
              <div className="text-xs text-muted-foreground mt-2">{done}/{total} hitos · {stagePct}%</div>

              {(total - done) === 1 && nextStage && (
                <div
                  className="mt-4 border-2 border-foreground bg-primary text-primary-foreground p-3 flex items-start gap-2 shadow-[3px_3px_0_0_hsl(var(--foreground))]"
                  data-testid={`stage-unlock-hint-${s.id}`}
                >
                  <Bell size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <div className="text-sm font-semibold leading-snug">
                    Te falta 1 hito para desbloquear <span className="whitespace-nowrap">{shortStageLabel(nextStage.name, nextIdx)}</span>.
                  </div>
                </div>
              )}

              <ul className="mt-4 divide-y-2 divide-foreground border-t-2 border-b-2 border-foreground">
                {s.milestones.length === 0 && (
                  <li className="py-3 text-sm text-muted-foreground">Sin hitos todavía. Agrega uno abajo.</li>
                )}
                {s.milestones.map((m) => (
                  <li key={m.id} className="py-3 flex items-center gap-3">
                    <button
                      onClick={() => toggleMilestone(s, m)}
                      data-testid={`milestone-toggle-${m.id}`}
                      aria-label="Marcar hito"
                    >
                      {m.completed
                        ? <CheckSquare size={20} weight="fill" />
                        : <Square size={20} weight="bold" />
                      }
                    </button>
                    <span className={`flex-1 text-sm ${m.completed ? "line-through opacity-60" : ""}`}>{m.name}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-3 flex items-center gap-2">
                <input
                  placeholder="Agregar hito…"
                  className="input-brut !py-2 flex-1 text-sm"
                  value={newMilestone[s.id] || ""}
                  onChange={(e) => setNewMilestone((prev) => ({ ...prev, [s.id]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMilestone(s.id); } }}
                  data-testid={`input-milestone-${s.id}`}
                />
                <button onClick={() => addMilestone(s.id)} className="btn-secondary flex items-center gap-2" data-testid={`btn-add-milestone-${s.id}`}>
                  <Plus size={14} weight="bold" /> Agregar
                </button>
              </div>

              <div className="mt-4">
                <div className="label-mono text-muted-foreground mb-1">Notas</div>
                {notesFor === s.id ? (
                  <div>
                    <textarea
                      className="input-brut !py-2 min-h-[80px] text-sm"
                      value={notesInput}
                      onChange={(e) => setNotesInput(e.target.value)}
                      data-testid={`textarea-notes-${s.id}`}
                    />
                    <div className="mt-2 flex gap-2">
                      <button onClick={() => saveNotes(s.id)} className="btn-primary" data-testid={`btn-save-notes-${s.id}`}>Guardar</button>
                      <button onClick={() => setNotesFor(null)} className="btn-ghost">Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => { setNotesFor(s.id); setNotesInput(s.notes || ""); }}
                    className="text-sm cursor-text min-h-[40px] border-2 border-dashed border-foreground/40 p-2 hover:border-foreground"
                    data-testid={`notes-view-${s.id}`}
                  >
                    {s.notes ? s.notes : <span className="text-muted-foreground">Haz clic para agregar una nota…</span>}
                  </div>
                )}
              </div>

              {nextStage && (
                <div className="mt-6 border-t-2 border-foreground pt-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="label-mono text-muted-foreground flex items-center gap-1">
                      <LockKey size={12} weight="bold" /> Al terminar esta etapa desbloqueas
                    </div>
                    <div className="font-semibold text-sm mt-1 truncate">{shortStageLabel(nextStage.name, nextIdx)}</div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    Próximamente <ArrowRight size={12} weight="bold" />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <div className="brut-card border-dashed">
          {!newStageOpen ? (
            <button onClick={() => setNewStageOpen(true)} className="btn-secondary flex items-center gap-2 mx-auto" data-testid="btn-new-stage">
              <Plus size={14} weight="bold" /> Agregar etapa
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                className="input-brut !py-2 flex-1"
                placeholder="Nombre de la etapa"
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStage(); } }}
                data-testid="input-new-stage"
              />
              <button onClick={addStage} className="btn-primary" data-testid="btn-save-stage">Crear</button>
              <button onClick={() => { setNewStageOpen(false); setNewStageName(""); }} className="btn-ghost"><X size={16} weight="bold" /></button>
            </div>
          )}
        </div>

        <RequisitosLocales />
      </div>
    </div>
  );
}
